// Block J (UI-22, 2026-10-11): All tasks, on the v2 kit (src/ui) with tasks.css: search and filters
// above, rows grouped by person or project in folding groups, stacked rows on a phone. Filters, sort,
// grouping, timers and requests are as they were. Fixed with it, each failing first in
// BlockJTasks.test.js:
//   - it never received the failed-load flag, so a failed load read "No tasks found";
//   - grouping and the member filter read assignedTeam as stored, so an assignee kept as { userId }
//     made a group called "[object Object]" and was missed by the filter. Both now go by the one
//     ownership rule (utils/taskTime: the assignees if any, otherwise the creator);
//   - "Unassigned" asked for a task with no assignee AND no creator, which no task is. It now shows
//     the tasks no one is assigned to.
// The phone's floating "+" moved into the page header's full-width "+ Add task" (addendum 4).
import React, { useState, useMemo, useRef, useEffect } from "react";
import { userMeetsPlan } from "./PlanGate";
import { useWorkspaceRoster } from "../hooks/useWorkspaceRoster";
import { api } from "../api/client";
import { PRIORITY_RANK } from "../utils/priorityColors";
import { isCalendarEventTask, taskDisplayTitle } from "../utils/calendarTask";   // Fresh-eyes 19
import { messageFromError } from '../utils/apiError';   // Block 233
import { showErrorToast, showToast } from '../utils/toast';        // Block 233; fix(toast): the shared toast
import { isTaskOverdue, isTaskResponsibility, taskAssigneeIds, formatTaskDay } from "../utils/taskTime"; // Block D20 Part A: overdue in the user's STORED timezone
import {
  Avatar, Button, ChipGroup, EmptyState as KitEmptyState, ErrorState, SearchInput, SegmentedControl, Select, StatusPill, useBreakpoint, AddTaskButton,
} from "../ui";
import { personTint } from "./Team/teamPeople";
import { TasksStandalone } from "./TasksShell";
import "./tasks.css";
import { MovedTag } from "./task/MovedTag";   // 2026-10-21: "Moved 3×"

const STATUS_CFG = {
  todo:       { label: "To do",       tone: "pending" },
  inProgress: { label: "In progress", tone: "neutral" },
  done:       { label: "Completed",   tone: "success" },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function getStatus(t) {
  if (t.completed)  return "done";
  if (t.inProgress) return "inProgress";
  return "todo";
}

// Block D20 Part A: compute overdue in the user's STORED timezone (set on render below),
// so the badge agrees with the Overdue filter and chat regardless of the browser's timezone.
function isOverdue(t) { return isTaskOverdue(t, isOverdue.tz); }

// "Today", "Tomorrow", "Yesterday", else "9 Oct" (the shared day formatter).
function fmtDate(dt) {
  const day = formatTaskDay(dt);
  if (!day) return null;
  const d = new Date(String(dt).slice(0, 10) + "T12:00:00");
  const now = new Date(); now.setHours(12, 0, 0, 0);
  const diff = Math.round((d - now) / 86400000);
  if (diff === 0)  return "Today";
  if (diff === 1)  return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return day;
}

function statusCycle(s) {
  return s === "todo" ? "inProgress" : s === "inProgress" ? "done" : "todo";
}
function statusToUpdates(s) {
  if (s === "done")       return { completed: true,  inProgress: false };
  if (s === "inProgress") return { completed: false, inProgress: true  };
  return                         { completed: false, inProgress: false };
}

// The person a task is grouped under: its first assignee, else its creator (the ownership rule).
function responsibleId(t) {
  const assignees = taskAssigneeIds(t);
  return assignees[0] ?? (t.userId != null ? String(t.userId) : null);
}

const priorityKey = (p) => {
  const k = String(p || "medium").toLowerCase();
  return ["low", "medium", "high"].includes(k) ? k : "medium";
};

function ProjectChip({ name, color }) {
  return (
    <span className="tk-project" title={name}>
      <span className="tk-project__swatch" style={color ? { '--tk-project': color } : undefined} aria-hidden="true" />
      <span className="tk-project__name">{name}</span>
    </span>
  );
}

// ── Timer button ──────────────────────────────────────────────────────────────
function TimerBtn({ taskId, task, activeTimers, onToggle, loggedMinutes }) {
  const running = !!activeTimers[taskId];
  const secs    = activeTimers[taskId]?.elapsed || 0;
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  const logged = loggedMinutes?.[String(taskId)] || 0;
  return (
    <span className="tk-timer">
      <Button size="sm" variant={running ? "dark" : "tertiary"} onClick={() => onToggle(taskId, task)}
        aria-label={`${running ? "Stop" : "Start"} the timer for "${task.title}"`}>
        {running ? "⏹ Stop" : "▶ Start"}
      </Button>
      {secs > 0 && <span className="tk-timer__time">{mm}:{ss}</span>}
      {!running && logged > 0 && (
        <span className="tk-timer__time">
          {Math.floor(logged / 60) > 0 ? `${Math.floor(logged / 60)}h ` : ""}{logged % 60 > 0 ? `${logged % 60}m` : ""} logged
        </span>
      )}
    </span>
  );
}

// ── Task row ──────────────────────────────────────────────────────────────────
function TaskRow({ task, onView, onMove, allUsers, showAvatar, isPro, activeTimers, onTimerToggle, loggedMinutes }) {
  const status     = getStatus(task);
  const overdue    = isOverdue(task);
  const assigneeId = responsibleId(task);
  const assignee   = allUsers.find(u => String(u.id) === assigneeId);
  const when       = (task.datetime || task.deadline) ? fmtDate(task.deadline || task.datetime) : null;
  const cfg        = STATUS_CFG[status];
  const meeting    = isCalendarEventTask(task);

  return (
    <div className={`tk-row${task.completed ? " tk-row--done" : ""}`}>
      {/* Fresh-eyes 19: a calendar event is a meeting, never a task with a priority. */}
      <span className="tk-row__dot"><span className={`tk-dot tk-dot--${meeting ? 'meeting' : priorityKey(task.priority)}`} aria-hidden="true" /></span>
      <button type="button" className="tk-row__main" onClick={() => onView(task)}>
        <span className="tk-row__title">{taskDisplayTitle(task)}</span>
        {(when || task.projectName || (showAvatar && assignee) || meeting) && (
          <span className="tk-row__meta">
            {meeting && <StatusPill tone="neutral">Meeting</StatusPill>}
            {when && (overdue ? <StatusPill tone="warning">Overdue · {when}</StatusPill> : <span>{when}</span>)}
            <MovedTag task={task} />
            {task.projectName && <ProjectChip name={task.projectName} color={task.projectColor} />}
            {showAvatar && assignee && <span>{assignee.name}</span>}
          </span>
        )}
      </button>
      <div className="tk-row__actions">
        {isPro && !task.completed && <TimerBtn taskId={task.id} task={task} activeTimers={activeTimers} onToggle={onTimerToggle} loggedMinutes={loggedMinutes} />}
        {!task.completed && (
          <Button size="sm" variant="confirm" onClick={() => onMove(task.id, { completed: true, inProgress: false })} aria-label={`Mark "${task.title}" done`}>✓ Done</Button>
        )}
        <Button size="sm" variant="tertiary" title="Move to the next status"
          aria-label={`${cfg.label}: move "${task.title}" to ${STATUS_CFG[statusCycle(status)].label}`}
          onClick={() => onMove(task.id, statusToUpdates(statusCycle(status)))}>
          {cfg.label}
        </Button>
      </div>
    </div>
  );
}

// ── Collapsible group section ─────────────────────────────────────────────────
function GroupSection({ icon, title, subtitle, count, tasks, onView, onMove, allUsers, showAvatar, defaultOpen = true, isPro, activeTimers, onTimerToggle, loggedMinutes, tint }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`tk-group${open ? "" : " tk-group--closed"}`} style={tint ? { '--tk-group': tint } : undefined}>
      <button type="button" className="tk-group__head" aria-expanded={open} onClick={() => setOpen(v => !v)}>
        {icon}
        <span className="tk-group__title">{title}</span>
        {subtitle && <span className="tk-group__sub">{subtitle}</span>}
        <StatusPill tone="neutral">{count}</StatusPill>
        <span className="tk-group__chev" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="tk-group__body">
          {tasks.length === 0
            ? <p className="tk-group__empty">No tasks here.</p>
            : tasks.map(t => <TaskRow key={t.id} task={t} onView={onView} onMove={onMove} allUsers={allUsers} showAvatar={showAvatar} isPro={isPro} activeTimers={activeTimers} onTimerToggle={onTimerToggle} loggedMinutes={loggedMinutes} />)
          }
        </div>
      )}
    </section>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ onAddTask }) {
  return (
    <KitEmptyState icon="📋" title="No tasks found"
      action={<AddTaskButton onClick={onAddTask}>+ Add a task</AddTaskButton>}>
      Try other filters, or add a new task to get started.
    </KitEmptyState>
  );
}

const VIEW_OPTIONS = [{ value: "list", label: "List" }, { value: "person", label: "Person" }, { value: "project", label: "Project" }];

// ── Main AllTasksView ─────────────────────────────────────────────────────────
export default function AllTasksView({ tasks, onViewTask, onMoveTask, onAddTask, currentUser, taskTypes, tasksLoadFailed = false, embedded = false }) {
  isOverdue.tz = currentUser?.timezone; // Block D20 Part A: stored timezone for the overdue helper
  const { members: allUsers } = useWorkspaceRoster();
  const isPro = userMeetsPlan(currentUser, 'pro'); // FIX-3: shared, case-insensitive, trial-aware
  const phone = useBreakpoint() === 'phone';

  const [search,         setSearch]         = useState("");
  const [viewMode,       setViewMode]       = useState("person");
  const [sortBy,         setSortBy]         = useState("priority");
  const [pill,           setPill]           = useState("all");
  const [memberFilter,   setMemberFilter]   = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [statusFilter,   setStatusFilter]   = useState(null);
  const [activeTimers,   setActiveTimers]   = useState({});
  const [loggedMinutes,  setLoggedMinutes]  = useState({});
  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const timerRefs = useRef({});

  useEffect(() => () => Object.values(timerRefs.current).forEach(clearInterval), []);

  async function saveTimeEntry(taskId, task, elapsed) {
    const durationMins = Math.round(elapsed / 60);
    if (durationMins < 1) return;
    const startDt = new Date(Date.now() - elapsed * 1000);
    try {
      await api('/api/time-entries', {
        method: 'POST',
        body: JSON.stringify({
          taskId: String(taskId),
          projectId: task?.projectId ?? null,
          startTime: startDt.toISOString(),
          endTime: new Date().toISOString(),
          duration: durationMins,
          note: null,
        }),
      });
      setLoggedMinutes(prev => ({ ...prev, [String(taskId)]: (prev[String(taskId)] || 0) + durationMins }));
    } catch (err) { showErrorToast(messageFromError(err, "Could not save time entry.")); }
  }

  function toggleTimer(taskId, task) {
    setActiveTimers(prev => {
      if (prev[taskId]) {
        clearInterval(timerRefs.current[taskId]);
        delete timerRefs.current[taskId];
        const elapsed = prev[taskId].elapsed;
        saveTimeEntry(taskId, task, elapsed);
        const next = { ...prev }; delete next[taskId]; return next;
      }
      const start = Date.now();
      timerRefs.current[taskId] = setInterval(() => {
        setActiveTimers(p => p[taskId] ? { ...p, [taskId]: { ...p[taskId], elapsed: Math.floor((Date.now() - start) / 1000) } } : p);
      }, 1000);
      return { ...prev, [taskId]: { start, elapsed: 0 } };
    });
  }

  function handleViewTask(task) {
    if (isPro && task?.projectId && !activeTimers[task.id]) {
      // fix(toast): the shared toast (it was its own white box, bottom right).
      showToast(`This is client work. Don't forget to start your timer for ${task.title || 'it'}.`, { type: 'info' });
    }
    onViewTask(task);
  }

  // ── Option lists ──
  const memberOptions = useMemo(() => {
    const ids = new Set(tasks.flatMap(t => [...taskAssigneeIds(t), ...(t.userId != null ? [String(t.userId)] : [])]));
    return allUsers
      .filter(u => ids.has(String(u.id)))
      .map(u => ({ value: String(u.id), label: u.name }));
  }, [tasks, allUsers]);

  const priorityOptions = ["High", "Medium", "Low"].map(p => ({ value: p, label: p }));
  const statusOptions = Object.entries(STATUS_CFG).map(([k, v]) => ({ value: k, label: v.label }));

  // ── Filter + sort ──
  const filtered = useMemo(() => {
    let list = [...tasks];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t => t.title?.toLowerCase().includes(q) || t.projectName?.toLowerCase().includes(q));
    }
    if (pill === "overdue")    list = list.filter(isOverdue);
    if (pill === "high")       list = list.filter(t => !isCalendarEventTask(t) && t.priority === "High");
    if (pill === "unassigned") list = list.filter(t => taskAssigneeIds(t).length === 0);
    if (memberFilter != null)  list = list.filter(t => isTaskResponsibility(t, memberFilter));
    if (priorityFilter)        list = list.filter(t => !isCalendarEventTask(t) && t.priority === priorityFilter);
    if (statusFilter)          list = list.filter(t => getStatus(t) === statusFilter);
    list.sort((a, b) => {
      if (sortBy === "priority") {
        const pa = PRIORITY_RANK[a.priority] ?? 2, pb = PRIORITY_RANK[b.priority] ?? 2;
        if (pa !== pb) return pa - pb;
      }
      if (sortBy === "dueDate")  return new Date(a.deadline || a.datetime || 0) - new Date(b.deadline || b.datetime || 0);
      if (sortBy === "created")  return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      return new Date(a.deadline || a.datetime || 0) - new Date(b.deadline || b.datetime || 0);
    });
    return list;
  }, [tasks, search, pill, memberFilter, priorityFilter, statusFilter, sortBy]);

  const totalActive = tasks.filter(t => !t.completed).length;
  const hasFilters  = memberFilter != null || priorityFilter || statusFilter || pill !== "all" || search.trim();
  const activeFilterCount = [memberFilter != null, !!priorityFilter, !!statusFilter, sortBy !== "priority", viewMode !== "person"].filter(Boolean).length;
  const clearAll = () => { setMemberFilter(null); setPriorityFilter(null); setStatusFilter(null); setPill("all"); setSearch(""); };

  // ── Group helpers ──
  function groupByPerson(list) {
    const unassigned = [], byId = {};
    list.forEach(t => {
      const aid = responsibleId(t);
      if (aid == null) { unassigned.push(t); return; }
      if (!byId[aid]) byId[aid] = [];
      byId[aid].push(t);
    });
    return { unassigned, byId };
  }

  function groupByProject(list) {
    const noProject = [], byName = {};
    list.forEach(t => {
      if (!t.projectName) { noProject.push(t); return; }
      if (!byName[t.projectName]) byName[t.projectName] = [];
      byName[t.projectName].push(t);
    });
    return { noProject, byName };
  }

  const { unassigned: pUnassigned, byId: pById }     = viewMode === "person"  ? groupByPerson(filtered)  : { unassigned: [], byId: {}   };
  const { noProject: prjNone,      byName: prjByName } = viewMode === "project" ? groupByProject(filtered) : { noProject: [], byName: {} };

  const CHIP_DEFS = [
    { id: "all",        label: "All"           },
    { id: "overdue",    label: "Overdue"       },
    { id: "high",       label: "High priority" },
    { id: "unassigned", label: "Unassigned"    },
  ];
  const rowProps = { onView: handleViewTask, onMove: onMoveTask, allUsers, isPro, activeTimers, onTimerToggle: toggleTimer, loggedMinutes };

  const selects = (
    <>
      {memberOptions.length > 0 && (
        <Select className="tk-filters__select" aria-label="Member" value={memberFilter ?? ""} onChange={e => setMemberFilter(e.target.value || null)}
          placeholder="All members" options={memberOptions} />
      )}
      <Select className="tk-filters__select" aria-label="Priority" value={priorityFilter ?? ""} onChange={e => setPriorityFilter(e.target.value || null)}
        placeholder="All priorities" options={priorityOptions} />
      <Select className="tk-filters__select" aria-label="Status" value={statusFilter ?? ""} onChange={e => setStatusFilter(e.target.value || null)}
        placeholder="All statuses" options={statusOptions} />
      <Select className="tk-filters__select" aria-label="Sort" value={sortBy} onChange={e => setSortBy(e.target.value)}
        options={[{ value: "priority", label: "Sort: Priority" }, { value: "dueDate", label: "Sort: Due date" }, { value: "created", label: "Sort: Created" }]} />
      <SegmentedControl label="Group by" options={VIEW_OPTIONS} value={viewMode} onChange={setViewMode} />
    </>
  );

  return (
    <TasksStandalone embedded={embedded} title="All tasks" subline={`${totalActive} active · ${filtered.length} shown`} onAddTask={onAddTask}>
      {embedded && (
        <div className="tk-bar">
          <div className="tk-bar__title">
            <h2>All tasks</h2>
            <p className="tk-muted">{totalActive} active · {filtered.length} shown</p>
          </div>
        </div>
      )}

      <div className="tk-filters">
        <SearchInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tasks" label="Search tasks" />
        {phone ? (
          <Button variant={activeFilterCount > 0 ? "secondary" : "tertiary"} aria-expanded={filtersOpen} onClick={() => setFiltersOpen(v => !v)}>
            {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : "Filters"}
          </Button>
        ) : selects}
        {phone && filtersOpen && selects}
        {hasFilters && <Button variant="destructive" onClick={clearAll}>✕ Clear filters</Button>}
      </div>

      <ChipGroup label="Show" value={pill} onChange={setPill} options={CHIP_DEFS.map(f => ({ value: f.id, label: f.label }))} />

      {/* A failed load is never "No tasks found" (Block J). */}
      {tasksLoadFailed ? (
        <div data-testid="all-tasks-load-failed">
          <ErrorState title="Your tasks could not be loaded."
            message="This is not an empty list, we could not reach your tasks. Check your connection and try again."
            onRetry={() => window.dispatchEvent(new Event('aidey:tasksUpdated'))} />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState onAddTask={onAddTask} />
      ) : (
        <div className="tk-stack">
          {/* LIST */}
          {viewMode === "list" && (
            <div className="tk-rows">
              {filtered.map(t => <TaskRow key={t.id} task={t} showAvatar {...rowProps} />)}
            </div>
          )}

          {/* GROUP BY PERSON */}
          {viewMode === "person" && (
            <>
              {pUnassigned.length > 0 && (
                <GroupSection icon={<span aria-hidden="true">📌</span>} title="Unassigned" count={pUnassigned.length} tasks={pUnassigned} showAvatar={false} {...rowProps} />
              )}
              {allUsers.filter(u => pById[String(u.id)]?.length > 0).map(u => (
                <GroupSection
                  key={u.id}
                  icon={<Avatar name={u.name} size={24} color={personTint(u.id)} />}
                  title={u.name}
                  subtitle={u.jobTitle || u.role || ""}
                  count={pById[String(u.id)].length}
                  tasks={pById[String(u.id)]}
                  showAvatar={false}
                  {...rowProps}
                />
              ))}
              {/* People on tasks but not in the active workspace's roster (the current user, or a
                  former member), named where we can. */}
              {Object.keys(pById)
                .filter(id => id && pById[id]?.length > 0 && !allUsers.find(u => String(u.id) === id))
                .map(id => {
                  const name = id === String(currentUser?.id) ? (currentUser?.name || 'You') : 'Someone no longer in this workspace';
                  return (
                    <GroupSection key={id} icon={<Avatar name={name} size={24} color={personTint(id)} />}
                      title={name} count={pById[id].length} tasks={pById[id]} showAvatar={false} {...rowProps} />
                  );
                })}
            </>
          )}

          {/* GROUP BY PROJECT */}
          {viewMode === "project" && (
            <>
              {prjNone.length > 0 && (
                <GroupSection icon={<span aria-hidden="true">📂</span>} title="No project" count={prjNone.length} tasks={prjNone} showAvatar defaultOpen={false} {...rowProps} />
              )}
              {Object.entries(prjByName).sort(([a], [b]) => a.localeCompare(b)).map(([name, ts]) => (
                <GroupSection key={name} icon={<span aria-hidden="true">📁</span>} title={name} count={ts.length} tasks={ts} showAvatar {...rowProps} />
              ))}
            </>
          )}
        </div>
      )}
    </TasksStandalone>
  );
}

